/**
 * API contracts for the `claims` module (M7 — claims + notifications).
 *
 * OWNERSHIP: the claims module agent owns this file exclusively. It is
 * pre-created (and pre-exported from ./index.ts) by the scaffold so filling it
 * in never requires touching a shared file. Import from '@warehouse/shared'.
 *
 * ---------------------------------------------------------------------------
 * A CLAIM IS A GROUP OF `claims` ROWS
 * ---------------------------------------------------------------------------
 * The Wave-1 schema (DATA-MODEL.md "orders") models `claims` as ONE row per
 * claimed article (`article_id`, `lot_id`, `qty_base`), while the module spec
 * (MODULES.md §M7, ported from the old complaints module) needs a claim with
 * per-line quantities under ONE status, description and photo set.
 *
 * Since module agents may not change the schema, a claim is represented as a
 * GROUP of rows that share a claim number:
 *
 *     REK-2607-0042      ← line 1 (the "header" row; carries the group fields)
 *     REK-2607-0042#2    ← line 2
 *     REK-2607-0042#3    ← line 3
 *
 * `claimNo` on the wire is always the group number (`REK-2607-0042`) and `id`
 * is always the header row's uuid. Everything below speaks that language; the
 * suffix never leaves the API. See `claim-groups.ts` in the API module.
 */
import { z } from 'zod';

import { listQueryParamsSchema, listResponseSchema, qtySchema, uuidSchema } from '../schemas/common.js';

// ---------------------------------------------------------------------------
// enums
// ---------------------------------------------------------------------------

/**
 * Everything the `claim_reason` DB enum can hold. `wrong_item` / `quality` are
 * legacy/other-module values kept readable; the portal only offers the six
 * reasons MODULES §M7 lists.
 */
export const claimReasonSchema = z.enum([
  'missing',
  'damaged',
  'spoiled',
  'cold_chain',
  'received_more',
  'wrong_item',
  'quality',
  'other',
]);
export type ClaimReason = z.infer<typeof claimReasonSchema>;

/** The reasons a user may pick when filing (old WMS `complaint_reason`). */
export const claimCreateReasonSchema = z.enum([
  'missing',
  'damaged',
  'spoiled',
  'cold_chain',
  'received_more',
  'other',
]);
export type ClaimCreateReason = z.infer<typeof claimCreateReasonSchema>;

export const CLAIM_CREATE_REASONS = claimCreateReasonSchema.options;

export const claimStatusSchema = z.enum(['registered', 'in_review', 'resolved', 'rejected']);
export type ClaimStatus = z.infer<typeof claimStatusSchema>;

/** The status a new claim always starts in (old WMS: "Mottagen"). */
export const CLAIM_INITIAL_STATUS: ClaimStatus = 'registered';

/**
 * Photo evidence buckets. `cold_chain` claims REQUIRE one signed freight note
 * (fraktsedel) plus one damaged-goods photo — enforced server-side.
 */
export const claimPhotoCategorySchema = z.enum(['freight_note', 'damaged_goods', 'other']);
export type ClaimPhotoCategory = z.infer<typeof claimPhotoCategorySchema>;

export const CLAIM_COLD_CHAIN_REQUIRED_PHOTOS: readonly ClaimPhotoCategory[] = [
  'freight_note',
  'damaged_goods',
];

// ---------------------------------------------------------------------------
// status catalog — editable, stored in `app_settings` under CLAIM_STATUS_CATALOG_KEY
// ---------------------------------------------------------------------------

export const CLAIM_STATUS_CATALOG_KEY = 'claims.status_catalog';

export const claimStatusLabelsSchema = z.object({
  sv: z.string().min(1).max(60),
  en: z.string().min(1).max(60),
  zh: z.string().min(1).max(60),
});
export type ClaimStatusLabels = z.infer<typeof claimStatusLabelsSchema>;

export const claimStatusCatalogEntrySchema = z.object({
  key: claimStatusSchema,
  labels: claimStatusLabelsSchema,
  /** Hex colour used for the status pill (old WMS seeded #3b82f6/#f59e0b/#10b981). */
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'colour must be #rrggbb'),
  sortOrder: z.number().int().min(0).max(999),
  /** Hidden from the pickers (never from existing rows — history stays readable). */
  active: z.boolean().default(true),
  description: z.string().max(200).default(''),
});
export type ClaimStatusCatalogEntry = z.infer<typeof claimStatusCatalogEntrySchema>;

export const claimStatusCatalogSchema = z.array(claimStatusCatalogEntrySchema).min(1).max(20);
export type ClaimStatusCatalog = z.infer<typeof claimStatusCatalogSchema>;

/**
 * Seed values — the old WMS's `complaint_statuses` rows, verbatim in Swedish
 * (`0013_fancy_agent_zero.sql`), plus `rejected` which the WMS enum adds.
 * Labels are editable per language; colours and order are editable too.
 */
export const CLAIM_STATUS_CATALOG_DEFAULT: ClaimStatusCatalog = [
  {
    key: 'registered',
    labels: { sv: 'Mottagen', en: 'Received', zh: '已接收' },
    color: '#3b82f6',
    sortOrder: 1,
    active: true,
    description: 'Reklamationen har tagits emot',
  },
  {
    key: 'in_review',
    labels: { sv: 'Under behandling', en: 'In review', zh: '处理中' },
    color: '#f59e0b',
    sortOrder: 2,
    active: true,
    description: 'Reklamationen behandlas',
  },
  {
    key: 'resolved',
    labels: { sv: 'Åtgärdad', en: 'Resolved', zh: '已解决' },
    color: '#10b981',
    sortOrder: 3,
    active: true,
    description: 'Reklamationen är åtgärdad',
  },
  {
    key: 'rejected',
    labels: { sv: 'Avvisad', en: 'Rejected', zh: '已驳回' },
    color: '#ef4444',
    sortOrder: 4,
    active: true,
    description: 'Reklamationen avvisades',
  },
];

export const claimStatusCatalogUpdateSchema = z.object({
  commandId: uuidSchema.optional(),
  statuses: claimStatusCatalogSchema,
});
export type ClaimStatusCatalogUpdate = z.infer<typeof claimStatusCatalogUpdateSchema>;

// ---------------------------------------------------------------------------
// read models
// ---------------------------------------------------------------------------

export const claimPhotoSchema = z.object({
  id: uuidSchema,
  category: claimPhotoCategorySchema,
  name: z.string(),
  mime: z.string(),
  size: z.number().int().nonnegative(),
  /** Always the module's own guarded route, never `/api/files/...`. */
  url: z.string(),
  uploadedBy: uuidSchema.nullable(),
  createdAt: z.iso.datetime(),
});
export type ClaimPhoto = z.infer<typeof claimPhotoSchema>;

export const claimLineSchema = z.object({
  /** The underlying `claims` row id — needed by nothing but audit trails. */
  id: uuidSchema,
  lineNo: z.number().int().min(1),
  articleId: uuidSchema.nullable(),
  articleNo: z.string().nullable(),
  articleName: z.string().nullable(),
  lotId: uuidSchema.nullable(),
  lotNo: z.string().nullable(),
  qtyBase: z.string().nullable(),
  baseUnitCode: z.string().nullable(),
  /** Ordered quantity on the matching sales-order line, when the claim has one. */
  orderedQtyBase: z.string().nullable(),
});
export type ClaimLine = z.infer<typeof claimLineSchema>;

/** What a signed-in user may do with this claim, decided server-side. */
export const claimAbilitiesSchema = z.object({
  canEdit: z.boolean(),
  canDelete: z.boolean(),
  canChangeStatus: z.boolean(),
  canAddPhotos: z.boolean(),
  /** true for warehouse staff holding `claims.manage` (no window, no lock). */
  isManager: z.boolean(),
  /** Self-service edit window end (48h) — null once an admin has taken over. */
  editWindowEndsAt: z.iso.datetime().nullable(),
  /** Self-service status window end (6h). */
  statusWindowEndsAt: z.iso.datetime().nullable(),
  /** true once the status left `registered`: the restaurant is locked out. */
  lockedByStatus: z.boolean(),
});
export type ClaimAbilities = z.infer<typeof claimAbilitiesSchema>;

export const claimListItemSchema = z.object({
  id: uuidSchema,
  claimNo: z.string(),
  restaurantId: uuidSchema,
  restaurantName: z.string().nullable(),
  salesOrderId: uuidSchema.nullable(),
  salesOrderNo: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  shipmentId: uuidSchema.nullable(),
  reason: claimReasonSchema,
  status: claimStatusSchema,
  description: z.string(),
  lineCount: z.number().int().nonnegative(),
  photoCount: z.number().int().nonnegative(),
  createdByName: z.string().nullable(),
  createdByEmail: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  resolvedAt: z.iso.datetime().nullable(),
});
export type ClaimListItem = z.infer<typeof claimListItemSchema>;

export const claimDetailSchema = claimListItemSchema.extend({
  resolutionNote: z.string().nullable(),
  resolvedBy: uuidSchema.nullable(),
  resolvedByName: z.string().nullable(),
  createdBy: uuidSchema.nullable(),
  lines: z.array(claimLineSchema),
  photos: z.array(claimPhotoSchema),
  abilities: claimAbilitiesSchema,
});
export type ClaimDetail = z.infer<typeof claimDetailSchema>;

export const claimListResponseSchema = listResponseSchema(claimListItemSchema);
export type ClaimListResponse = z.infer<typeof claimListResponseSchema>;

/** `GET /api/claims` — the shared list contract plus claim-specific filters. */
export const claimListQuerySchema = listQueryParamsSchema;
export type ClaimListQuery = z.infer<typeof claimListQuerySchema>;

/** Filter keys the endpoint accepts (mirrored by the API's listQuery config). */
export const CLAIM_FILTER_KEYS = [
  'status',
  'reason',
  'restaurantId',
  'salesOrderId',
  'articleId',
  'createdFrom',
  'createdTo',
  'mine',
] as const;

/** Sort keys the endpoint accepts. */
export const CLAIM_SORT_KEYS = ['createdAt', 'claimNo', 'status', 'reason', 'restaurant'] as const;

// ---------------------------------------------------------------------------
// claimable order lines (the portal's "what did you receive wrong?" picker)
// ---------------------------------------------------------------------------

export const claimableOrderLineSchema = z.object({
  orderLineId: uuidSchema,
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  qtyBase: z.string(),
  baseUnitCode: z.string().nullable(),
  /** Base-unit quantity already claimed on this order+article. */
  claimedQtyBase: z.string(),
});
export type ClaimableOrderLine = z.infer<typeof claimableOrderLineSchema>;

export const claimableOrderSchema = z.object({
  salesOrderId: uuidSchema,
  orderNo: z.string(),
  restaurantId: uuidSchema,
  restaurantName: z.string().nullable(),
  deliveryDate: z.string(),
  status: z.string(),
  lines: z.array(claimableOrderLineSchema),
});
export type ClaimableOrder = z.infer<typeof claimableOrderSchema>;

// ---------------------------------------------------------------------------
// write models — every one of these is a CommandBus payload
// ---------------------------------------------------------------------------

export const claimLineInputSchema = z.object({
  articleId: uuidSchema,
  lotId: uuidSchema.nullish(),
  qtyBase: qtySchema,
});
export type ClaimLineInput = z.infer<typeof claimLineInputSchema>;

export const claimPhotoInputSchema = z.object({
  fileId: uuidSchema,
  category: claimPhotoCategorySchema,
});
export type ClaimPhotoInput = z.infer<typeof claimPhotoInputSchema>;

export const claimCreatePayloadSchema = z.object({
  restaurantId: uuidSchema,
  salesOrderId: uuidSchema.nullish(),
  shipmentId: uuidSchema.nullish(),
  /**
   * The shipment's `qr_token` from the packing-slip QR. Compared in CONSTANT
   * TIME against the stored token — the old WMS's claim endpoints were public
   * and compared tokens with `===`.
   */
  shipmentToken: z.string().min(8).max(200).nullish(),
  reason: claimCreateReasonSchema,
  description: z.string().trim().max(4000).default(''),
  lines: z.array(claimLineInputSchema).min(1).max(200),
  photos: z.array(claimPhotoInputSchema).max(20).default([]),
});
export type ClaimCreatePayload = z.infer<typeof claimCreatePayloadSchema>;

export const claimCreateRequestSchema = claimCreatePayloadSchema.extend({
  commandId: uuidSchema.optional(),
});
export type ClaimCreateRequest = z.infer<typeof claimCreateRequestSchema>;

export const claimUpdatePayloadSchema = z.object({
  claimId: uuidSchema,
  reason: claimCreateReasonSchema.optional(),
  description: z.string().trim().max(4000).optional(),
  salesOrderId: uuidSchema.nullish(),
  /** Replaces the whole line set when present (the editor is a full form). */
  lines: z.array(claimLineInputSchema).min(1).max(200).optional(),
});
export type ClaimUpdatePayload = z.infer<typeof claimUpdatePayloadSchema>;

export const claimUpdateRequestSchema = claimUpdatePayloadSchema.omit({ claimId: true }).extend({
  commandId: uuidSchema.optional(),
});
export type ClaimUpdateRequest = z.infer<typeof claimUpdateRequestSchema>;

export const claimStatusChangePayloadSchema = z.object({
  claimId: uuidSchema,
  status: claimStatusSchema,
  resolutionNote: z.string().trim().max(4000).nullish(),
});
export type ClaimStatusChangePayload = z.infer<typeof claimStatusChangePayloadSchema>;

export const claimStatusChangeRequestSchema = claimStatusChangePayloadSchema
  .omit({ claimId: true })
  .extend({ commandId: uuidSchema.optional() });
export type ClaimStatusChangeRequest = z.infer<typeof claimStatusChangeRequestSchema>;

export const claimDeletePayloadSchema = z.object({ claimId: uuidSchema });
export type ClaimDeletePayload = z.infer<typeof claimDeletePayloadSchema>;

export const claimPhotoAttachPayloadSchema = z.object({
  claimId: uuidSchema,
  fileId: uuidSchema,
  category: claimPhotoCategorySchema,
});
export type ClaimPhotoAttachPayload = z.infer<typeof claimPhotoAttachPayloadSchema>;

export const claimPhotoDetachPayloadSchema = z.object({
  claimId: uuidSchema,
  fileId: uuidSchema,
});
export type ClaimPhotoDetachPayload = z.infer<typeof claimPhotoDetachPayloadSchema>;

/** Response of every claim mutation — the fresh detail plus the command verdict. */
export const claimMutationResultSchema = z.object({
  claimId: uuidSchema,
  claimNo: z.string(),
  status: claimStatusSchema,
});
export type ClaimMutationResult = z.infer<typeof claimMutationResultSchema>;

// ---------------------------------------------------------------------------
// command types — registered with the CommandBus, so the same payloads work
// from the web AND from `POST /api/sync/commands` (offline portal / scanner).
// ---------------------------------------------------------------------------

export const CLAIM_COMMANDS = {
  create: 'claim.create',
  update: 'claim.update',
  status: 'claim.status',
  remove: 'claim.delete',
  attachPhoto: 'claim.photoAttach',
  detachPhoto: 'claim.photoDetach',
  statusCatalog: 'claim.statusCatalog',
  notificationRead: 'notification.read',
  notificationReadAll: 'notification.readAll',
} as const;

export type ClaimCommandType = (typeof CLAIM_COMMANDS)[keyof typeof CLAIM_COMMANDS];

// ---------------------------------------------------------------------------
// statistics (spec §5.11)
// ---------------------------------------------------------------------------

export const claimStatsQuerySchema = z.object({
  /** ISO date (inclusive). Defaults to 12 months back. */
  from: z.iso.date().optional(),
  /** ISO date (inclusive). Defaults to today. */
  to: z.iso.date().optional(),
  restaurantId: uuidSchema.optional(),
  articleId: uuidSchema.optional(),
  reason: claimReasonSchema.optional(),
  status: claimStatusSchema.optional(),
  /** Rows returned per top-N table. */
  limit: z.coerce.number().int().min(1).max(100).default(10),
});
export type ClaimStatsQuery = z.infer<typeof claimStatsQuerySchema>;

const bucketSchema = z.object({
  key: z.string(),
  label: z.string().nullable(),
  claims: z.number().int().nonnegative(),
  lines: z.number().int().nonnegative(),
  qtyBase: z.string(),
});
export type ClaimStatsBucket = z.infer<typeof bucketSchema>;

export const claimStatsSchema = z.object({
  from: z.iso.date(),
  to: z.iso.date(),
  totals: z.object({
    claims: z.number().int().nonnegative(),
    lines: z.number().int().nonnegative(),
    qtyBase: z.string(),
    open: z.number().int().nonnegative(),
    resolved: z.number().int().nonnegative(),
  }),
  byMonth: z.array(bucketSchema),
  byReason: z.array(bucketSchema),
  byStatus: z.array(bucketSchema),
  byRestaurant: z.array(bucketSchema),
  byArticle: z.array(bucketSchema),
});
export type ClaimStats = z.infer<typeof claimStatsSchema>;

export const claimStatsDimensionSchema = z.enum(['article', 'restaurant', 'reason', 'month', 'status']);
export type ClaimStatsDimension = z.infer<typeof claimStatsDimensionSchema>;

export const claimStatsTopQuerySchema = claimStatsQuerySchema.extend({
  dimension: claimStatsDimensionSchema,
  /** Rank by number of claims or by claimed base quantity. */
  metric: z.enum(['claims', 'lines', 'qty']).default('claims'),
});
export type ClaimStatsTopQuery = z.infer<typeof claimStatsTopQuerySchema>;

export const claimStatsTopSchema = z.object({
  dimension: claimStatsDimensionSchema,
  metric: z.enum(['claims', 'lines', 'qty']),
  items: z.array(bucketSchema),
});
export type ClaimStatsTop = z.infer<typeof claimStatsTopSchema>;

// ---------------------------------------------------------------------------
// notification centre
// ---------------------------------------------------------------------------

/**
 * Known `notifications.type` values. The column is free text so other modules
 * can add their own without a migration; these are the ones M7 renders with a
 * dedicated icon + sentence.
 */
export const NOTIFICATION_TYPES = {
  claimCreated: 'claim.created',
  claimStatusChanged: 'claim.status_changed',
  claimResolved: 'claim.resolved',
  receptionDamaged: 'reception.damaged',
  orderConfirmed: 'order.confirmed',
  shipmentDispatched: 'shipment.dispatched',
} as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[keyof typeof NOTIFICATION_TYPES];

export const notificationSchema = z.object({
  id: uuidSchema,
  type: z.string(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  readAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  /** null when the row is a role fan-out rather than a personal notification. */
  userId: uuidSchema.nullable(),
  roleKey: z.string().nullable(),
});
export type Notification = z.infer<typeof notificationSchema>;

export const notificationListResponseSchema = listResponseSchema(notificationSchema).extend({
  unread: z.number().int().nonnegative(),
});
export type NotificationListResponse = z.infer<typeof notificationListResponseSchema>;

export const notificationUnreadCountSchema = z.object({
  unread: z.number().int().nonnegative(),
});
export type NotificationUnreadCount = z.infer<typeof notificationUnreadCountSchema>;

export const notificationReadPayloadSchema = z.object({ notificationId: uuidSchema });
export type NotificationReadPayload = z.infer<typeof notificationReadPayloadSchema>;

export const notificationReadAllPayloadSchema = z.object({
  /** Only mark rows created at or before this instant (avoids racing new ones). */
  until: z.iso.datetime().optional(),
});
export type NotificationReadAllPayload = z.infer<typeof notificationReadAllPayloadSchema>;

/** `mail_outbox.template` ids owned by M7 (the four transactional emails). */
export const CLAIM_MAIL_TEMPLATES = {
  claimReceived: 'claim-received',
  claimResolved: 'claim-resolved',
  orderConfirmation: 'order-confirmation',
  shipmentDispatched: 'shipment-dispatched',
} as const;

export type ClaimMailTemplate = (typeof CLAIM_MAIL_TEMPLATES)[keyof typeof CLAIM_MAIL_TEMPLATES];
